import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

export type ScreeningStatus = 'Completed' | 'In progress' | 'Invite sent';
export type ScreeningRecommendation = 'recommended' | 'review' | 'not_recommended' | 'pending';
export type CheckResult = 'clear' | 'none' | 'records_found' | 'pending';

// A tenant screening — one FCRA-regulated background/credit report tied to a
// person. Lives on its own so it can be listed in the Screenings section and
// surfaced on the individual guest/tenant record.
export interface TenantScreening {
  id: number;
  applicant_name: string;
  email: string;
  phone: string;
  property_name: string;
  applicant_type: 'Applicant' | 'Tenant';
  requested_at: string;
  completed_at: string | null;
  status: ScreeningStatus;
  recommendation: ScreeningRecommendation;
  credit_score: number | null;
  monthly_income: number | null;
  monthly_rent: number | null;
  income_to_rent_ratio: number | null;
  income_verified: boolean;
  criminal: CheckResult;
  eviction: CheckResult;
  provider: string;
}

// Keyed to the people in the Guests/Tenants contact list (matching emails) so a
// screening can be opened both from the Screenings section and a person record.
const DUMMY_SCREENINGS: TenantScreening[] = [
  {
    id: 1, applicant_name: 'Sarah Mitchell', email: 'sarah.mitchell@example.com', phone: '+1 (415) 555-0101',
    property_name: 'City View Condo', applicant_type: 'Tenant', requested_at: '2026-07-20', completed_at: '2026-07-21',
    status: 'Completed', recommendation: 'recommended', credit_score: 762, monthly_income: 8400, monthly_rent: 2450,
    income_to_rent_ratio: 3.4, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 2, applicant_name: 'James Anderson', email: 'james.a@example.com', phone: '+1 (415) 555-0102',
    property_name: 'Mountain Retreat', applicant_type: 'Tenant', requested_at: '2026-07-18', completed_at: '2026-07-19',
    status: 'Completed', recommendation: 'recommended', credit_score: 709, monthly_income: 7200, monthly_rent: 2400,
    income_to_rent_ratio: 3.0, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 3, applicant_name: 'Priya Sharma', email: 'priya.sharma@example.com', phone: '+1 (415) 555-0103',
    property_name: 'Cozy Cottage', applicant_type: 'Applicant', requested_at: '2026-07-22', completed_at: '2026-07-23',
    status: 'Completed', recommendation: 'review', credit_score: 663, monthly_income: 5000, monthly_rent: 2000,
    income_to_rent_ratio: 2.5, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 4, applicant_name: 'Michael Chen', email: 'm.chen@example.com', phone: '+1 (415) 555-0104',
    property_name: 'Desert Oasis', applicant_type: 'Applicant', requested_at: '2026-07-31', completed_at: null,
    status: 'In progress', recommendation: 'pending', credit_score: null, monthly_income: null, monthly_rent: 2600,
    income_to_rent_ratio: null, income_verified: false, criminal: 'pending', eviction: 'pending', provider: 'TransUnion SmartMove',
  },
  {
    id: 5, applicant_name: 'Emily Rodriguez', email: 'emily.r@example.com', phone: '+1 (415) 555-0105',
    property_name: 'Secluded Cabin', applicant_type: 'Tenant', requested_at: '2026-07-10', completed_at: '2026-07-11',
    status: 'Completed', recommendation: 'recommended', credit_score: 795, monthly_income: 9600, monthly_rent: 2350,
    income_to_rent_ratio: 4.1, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 6, applicant_name: 'David Park', email: 'david.park@example.com', phone: '+1 (415) 555-0106',
    property_name: 'Beach Bungalow', applicant_type: 'Applicant', requested_at: '2026-07-15', completed_at: '2026-07-16',
    status: 'Completed', recommendation: 'not_recommended', credit_score: 591, monthly_income: 4200, monthly_rent: 2100,
    income_to_rent_ratio: 2.0, income_verified: true, criminal: 'clear', eviction: 'records_found', provider: 'TransUnion SmartMove',
  },
  {
    id: 7, applicant_name: 'Olivia Bennett', email: 'olivia.b@example.com', phone: '+1 (415) 555-0107',
    property_name: 'Cadence Cottage', applicant_type: 'Tenant', requested_at: '2026-07-05', completed_at: '2026-07-06',
    status: 'Completed', recommendation: 'recommended', credit_score: 741, monthly_income: 8800, monthly_rent: 2750,
    income_to_rent_ratio: 3.2, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 8, applicant_name: 'Noah Wilson', email: 'noah.w@example.com', phone: '+1 (415) 555-0108',
    property_name: 'Modern Apartment', applicant_type: 'Applicant', requested_at: '2026-08-02', completed_at: null,
    status: 'Invite sent', recommendation: 'pending', credit_score: null, monthly_income: null, monthly_rent: 2200,
    income_to_rent_ratio: null, income_verified: false, criminal: 'pending', eviction: 'pending', provider: 'TransUnion SmartMove',
  },
  // --- Room-splitting demo: per-member screenings, Palmetto House (unit 9) ---
  {
    id: 9, applicant_name: 'Marcus Whitfield', email: 'marcus.whitfield@example.com', phone: '+1 (813) 555-0201',
    property_name: 'Palmetto House', applicant_type: 'Tenant', requested_at: '2026-04-28', completed_at: '2026-04-29',
    status: 'Completed', recommendation: 'recommended', credit_score: 701, monthly_income: 3600, monthly_rent: 975,
    income_to_rent_ratio: 3.7, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 10, applicant_name: 'Keisha Boone', email: 'keisha.boone@example.com', phone: '+1 (813) 555-0202',
    property_name: 'Palmetto House', applicant_type: 'Tenant', requested_at: '2026-01-28', completed_at: '2026-01-29',
    status: 'Completed', recommendation: 'recommended', credit_score: 718, monthly_income: 3100, monthly_rent: 780,
    income_to_rent_ratio: 4.0, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 11, applicant_name: 'Felix Ostrander', email: 'felix.ostrander@example.com', phone: '+1 (813) 555-0203',
    property_name: 'Palmetto House', applicant_type: 'Tenant', requested_at: '2026-01-10', completed_at: '2026-01-12',
    status: 'Completed', recommendation: 'recommended', credit_score: 689, monthly_income: 3300, monthly_rent: 825,
    income_to_rent_ratio: 4.0, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  // --- Room-splitting demo: per-member screenings, Ferncreek Rooming House (unit 10) ---
  {
    id: 12, applicant_name: 'Renata Cho', email: 'renata.cho@example.com', phone: '+1 (614) 555-0204',
    property_name: 'Ferncreek Rooming House', applicant_type: 'Tenant', requested_at: '2025-10-28', completed_at: '2025-10-29',
    status: 'Completed', recommendation: 'recommended', credit_score: 726, monthly_income: 2900, monthly_rent: 700,
    income_to_rent_ratio: 4.1, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
  {
    id: 13, applicant_name: 'Dwayne Pruitt', email: 'dwayne.pruitt@example.com', phone: '+1 (614) 555-0205',
    property_name: 'Ferncreek Rooming House', applicant_type: 'Tenant', requested_at: '2025-07-28', completed_at: '2025-07-29',
    status: 'Completed', recommendation: 'review', credit_score: 641, monthly_income: 2350, monthly_rent: 675,
    income_to_rent_ratio: 3.5, income_verified: true, criminal: 'clear', eviction: 'none', provider: 'TransUnion SmartMove',
  },
];

@Injectable({
  providedIn: 'root'
})
export class TenantScreeningService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  // Screening has no backend yet — the sample reports belong only to the demo
  // account; real accounts must see an empty pipeline, never fabricated data.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  async getScreenings(): Promise<TenantScreening[]> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_SCREENINGS : []; }
    return firstValueFrom(this.http.get<TenantScreening[]>(`${environment.api_url}api/v1/customer/tenant-screenings`));
  }

  async getScreening(id: number): Promise<TenantScreening | undefined> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_SCREENINGS.find(s => s.id === id) : undefined; }
    return firstValueFrom(this.http.get<TenantScreening>(`${environment.api_url}api/v1/customer/tenant-screenings/${id}`));
  }

  async getScreeningByEmail(email: string): Promise<TenantScreening | undefined> {
    if (!email) { return undefined; }
    const screenings = await this.getScreenings();
    return screenings.find(s => s.email.toLowerCase() === email.toLowerCase());
  }

  // Invite a person to complete an FCRA screening — creates a pending record.
  async requestScreening(person: { name: string; email: string; phone?: string; property_name?: string }): Promise<TenantScreening> {
    const existing = await this.getScreeningByEmail(person.email);
    if (existing) {
      existing.status = 'Invite sent';
      return existing;
    }
    const screening: TenantScreening = {
      id: Math.max(0, ...DUMMY_SCREENINGS.map(s => s.id)) + 1,
      applicant_name: person.name,
      email: person.email,
      phone: person.phone || '',
      property_name: person.property_name || '',
      applicant_type: 'Applicant',
      requested_at: new Date().toISOString().slice(0, 10),
      completed_at: null,
      status: 'Invite sent',
      recommendation: 'pending',
      credit_score: null,
      monthly_income: null,
      monthly_rent: null,
      income_to_rent_ratio: null,
      income_verified: false,
      criminal: 'pending',
      eviction: 'pending',
      provider: 'TransUnion SmartMove',
    };
    DUMMY_SCREENINGS.unshift(screening);
    return screening;
  }

  // ---- Shared label / color helpers (FCRA report vocabulary) ----
  recommendationLabel(rec: ScreeningRecommendation): string {
    switch (rec) {
      case 'recommended': return 'Recommended';
      case 'review': return 'Review Recommended';
      case 'not_recommended': return 'Not Recommended';
      default: return 'Screening In Progress';
    }
  }

  recommendationColor(rec: ScreeningRecommendation): string {
    switch (rec) {
      case 'recommended': return '#2E7D32';
      case 'review': return '#F9A825';
      case 'not_recommended': return '#C62828';
      default: return '#999';
    }
  }

  statusColor(status: ScreeningStatus): string {
    switch (status) {
      case 'Completed': return '#2E7D32';
      case 'In progress': return '#F9A825';
      case 'Invite sent': return '#999';
      default: return '#1E2125';
    }
  }

  creditBand(score: number | null): string {
    if (score === null) { return ''; }
    if (score >= 740) { return 'Excellent'; }
    if (score >= 670) { return 'Good'; }
    if (score >= 580) { return 'Fair'; }
    return 'Poor';
  }

  checkColor(value: CheckResult): string {
    switch (value) {
      case 'clear':
      case 'none': return '#2E7D32';
      case 'records_found': return '#C62828';
      default: return '#999';
    }
  }

  checkLabel(value: CheckResult): string {
    switch (value) {
      case 'clear': return 'Clear — no records';
      case 'none': return 'None found';
      case 'records_found': return 'Records found';
      case 'pending': return 'Pending';
      default: return value;
    }
  }
}
